/**
 * Clients API Hook
 *
 * Hook for fetching client data from the authenticated API.
 */

import { useApiData } from './client';
import type { Client } from './types';

/**
 * Hook for fetching the list of clients.
 *
 * Automatically fetches clients on mount and handles authentication.
 *
 * @param options - Optional query parameters
 * @returns Client list data, loading state, error, and refetch function
 */
export function useClients(options?: {
  skip?: number;
  limit?: number;
  orderBy?: string;
  ascending?: boolean;
  withMatters?: boolean;
  withProjects?: boolean;
  withVectors?: boolean;
}) {
  // Build query string from options
  const params = new URLSearchParams();
  if (options?.skip !== undefined) params.set('skip', String(options.skip));
  if (options?.limit !== undefined) params.set('limit', String(options.limit));
  if (options?.orderBy) params.set('order_by', options.orderBy);
  if (options?.ascending !== undefined) params.set('ascending', String(options.ascending));
  if (options?.withMatters) params.set('with_matters', 'true');
  if (options?.withProjects) params.set('with_projects', 'true');
  if (options?.withVectors) params.set('with_vectors', 'true');

  const queryString = params.toString();
  const endpoint = queryString ? `/clients?${queryString}` : '/clients';

  return useApiData<Client[]>(endpoint);
}

/**
 * Hook for fetching a single client by ID.
 *
 * @param clientId - The client ID to fetch
 * @returns Client data, loading state, error, and refetch function
 */
export function useClient(clientId: number) {
  return useApiData<Client>(`/clients/${clientId}`);
}
